import { ReactNode } from 'react'
import { ArrowRightIcon, EnvelopeIcon, LifebuoyIcon, PhoneIcon } from '@heroicons/react/24/outline'
import PageMasthead from '../components/PageMasthead'
import { useTitle } from '../components/TitleProvider'

// Brand motion: 200ms on the Arwen easing curve. Shared so buttons, links and the
// arrow nudge all move as one system rather than each picking its own timing.
const motion = 'transition-colors duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]'
// Red focus ring, applied everywhere and never removed — matches the app-wide
// focus:ring-arwen-500 convention used across the reskin.
const focusRing = 'focus:outline-none focus:ring-2 focus:ring-arwen-500 focus:ring-offset-2'

const buttonBase = `group inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium shadow-sm ${motion} ${focusRing}`
// Primary is the brand red (the only saturated colour in Arwen UI chrome); hover
// deepens to arwen-700 with no scale. Ghost is the white/ink-border secondary, and
// fills to ink-100 on hover while the border holds.
const primaryButton = `${buttonBase} border border-transparent bg-arwen-600 text-white hover:bg-arwen-700`
const ghostButton = `${buttonBase} border border-ink-200 bg-white text-ink-800 hover:bg-ink-100`

interface ContactCardProps {
    /** UPPERCASE tracked section label, e.g. EMAIL. */
    eyebrow: string
    /** Decorative glyph shown in the red-wash tile; the card's link carries the name. */
    icon: ReactNode
    /** The contact value — email address or phone number. */
    value: ReactNode
    helper: string
    /** The primary/ghost call-to-action link (mailto: / tel:). */
    action: ReactNode
}

/**
 * A single contact method: red-wash icon tile, eyebrow, value, helper line and a
 * call-to-action. The tile is aria-hidden so screen readers land on the action link's
 * accessible name rather than announcing a decorative icon twice.
 */
function ContactCard({ eyebrow, icon, value, helper, action }: ContactCardProps) {
    return (
        <div className="flex flex-col rounded-lg border border-ink-200 bg-white p-7 shadow-sm">
            <div
                aria-hidden="true"
                className="flex h-12 w-12 items-center justify-center rounded-lg bg-arwen-50 text-arwen-700"
            >
                {icon}
            </div>
            <p className="mt-5 text-xs font-medium uppercase tracking-wider text-ink-500">
                {eyebrow}
            </p>
            <p className="mt-1 text-xl font-medium text-ink-900">{value}</p>
            <p className="mt-2 text-sm text-ink-500">{helper}</p>
            <div className="mt-5">{action}</div>
        </div>
    )
}

function SupportPage() {
    useTitle('Support')
    return (
        <div className="min-h-full bg-ink-50">
            <PageMasthead title="Support" subtitle="We're here to help — here's how to get in touch" />
            <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 lg:px-8">
                {/* Contact-first: cards reflow to a single column below the sm (640px) breakpoint. */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <ContactCard
                        eyebrow="Email"
                        icon={<EnvelopeIcon className="h-6 w-6" />}
                        value="support@arwen.ai"
                        helper="We typically reply within one business day."
                        action={
                            <a href="mailto:support@arwen.ai" className={primaryButton}>
                                Email us
                                <ArrowRightIcon
                                    aria-hidden="true"
                                    className={`h-4 w-4 transition-transform duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:translate-x-0.5`}
                                />
                            </a>
                        }
                    />
                    <ContactCard
                        eyebrow="Telephone"
                        icon={<PhoneIcon className="h-6 w-6" />}
                        value={<span className="tabular-nums">+44 (0) 207 139 5083</span>}
                        helper="Mon–Fri, 9:00–17:30 GMT."
                        action={
                            <a href="tel:+442071395083" className={ghostButton}>
                                Call now
                            </a>
                        }
                    />
                </div>

                {/* Self-serve bar: stacks below sm, then sits inline with the link pushed right. */}
                <div className="mt-6 flex flex-col gap-3 rounded-lg border border-ink-200 bg-white px-7 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <div
                            aria-hidden="true"
                            className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-ink-100 text-ink-500"
                        >
                            <LifebuoyIcon className="h-5 w-5" />
                        </div>
                        <p className="text-sm text-ink-700">
                            Prefer to self-serve? Browse FAQs and guides in the Help Centre.
                        </p>
                    </div>
                    <a
                        href="https://knowledge.arwen.ai/"
                        target="_blank"
                        rel="noreferrer"
                        className={`group inline-flex flex-none items-center gap-1.5 rounded-md text-sm font-medium text-arwen-700 ${motion} hover:text-arwen-800 ${focusRing}`}
                    >
                        Open Help Centre
                        <ArrowRightIcon
                            aria-hidden="true"
                            className="h-4 w-4 transition-transform duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:translate-x-0.5"
                        />
                    </a>
                </div>
            </div>
        </div>
    )
}

export default SupportPage
